import { analyzeWindow, countdownLabel, opensAfterToday } from './analyzeHours';

/** Pure state of the Analyze card. The database decides who may run; this decides what to show. */
export interface TodayReport {
  id: string;
  report_day: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'expired';
  started_by_name: string;
  /** When the request was made. */
  started_at: string;
  /** When Kyu's Mac picked it up. Null while it waits. */
  picked_up_at: string | null;
  error: string | null;
}
export interface AnalyzeInput {
  isTopMember: boolean;
  reports: TodayReport[];
  now: Date;
}
export type AnalyzeKind = 'ready' | 'running' | 'waiting' | 'used' | 'failed' | 'closed' | 'denied';
export type AnalyzeStep = 'sent' | 'writing';
export interface AnalyzeState {
  kind: AnalyzeKind;
  canPress: boolean;
  primary: string;
  /** Who started today's report (or the last try), and when in Manila. */
  by: string | null;
  at: string | null;
  /** Time until the next report may start, for example "22 h 48 m". */
  resetIn: string;
  /** Which step of the progress list is live, while a request is in flight. */
  step: AnalyzeStep | null;
  /** Whole minutes a request has waited, while it waits. */
  minutesWaiting: number | null;
}
/** The server's rules, copied so the screen agrees with the sweep (20270239000100). */
export const STILL_WAITING_MS = 10 * 60_000;
export const RUN_TIMEOUT_MS = 12 * 60_000;

function manilaClock(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
}

export function analyzeState({ isTopMember, reports, now }: AnalyzeInput): AnalyzeState {
  const win = analyzeWindow(now);
  const nowMs = now.getTime();
  const todays = reports
    .filter((r) => r.report_day === win.nyDate)
    .sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at));
  const done = todays.find((r) => r.status === 'done');
  // A queued request left at 2 PM ET is expired by the server; a run left over
  // twelve minutes is failed. Show them that way before the server sweep lands.
  const live = todays.find((r) => {
    if (r.status === 'queued') return win.open;
    if (r.status === 'running')
      return nowMs - Date.parse(r.picked_up_at ?? r.started_at) <= RUN_TIMEOUT_MS;
    return false;
  });
  const shown = done ?? live ?? todays[0];
  const nextOpen = win.opensAt ?? opensAfterToday(now);
  const base = {
    by: shown?.started_by_name ?? null,
    at: shown ? manilaClock(shown.started_at) : null,
    resetIn: countdownLabel(nextOpen.getTime() - nowMs),
    step: null as AnalyzeStep | null,
    minutesWaiting: null as number | null,
  };
  if (!isTopMember)
    return { kind: 'denied', canPress: false, primary: 'Read past reports', ...base };
  if (done) return { kind: 'used', canPress: false, primary: "Read today's report", ...base };
  if (live?.status === 'running')
    return { kind: 'running', canPress: false, primary: 'Analyzing', ...base, step: 'writing' };
  if (live?.status === 'queued') {
    const waited = nowMs - Date.parse(live.started_at);
    if (waited >= STILL_WAITING_MS)
      return {
        kind: 'waiting',
        canPress: false,
        primary: 'Still waiting',
        ...base,
        step: 'sent',
        minutesWaiting: Math.floor(waited / 60_000),
      };
    return { kind: 'running', canPress: false, primary: 'Analyzing', ...base, step: 'sent' };
  }
  if (!win.open)
    return {
      kind: 'closed',
      canPress: false,
      primary: `Opens 6 AM ET, in ${base.resetIn}`,
      ...base,
    };
  const last = todays[0];
  if (last && (last.status === 'failed' || last.status === 'running'))
    return { kind: 'failed', canPress: true, primary: 'Try again', ...base };
  return { kind: 'ready', canPress: true, primary: 'Analyze now', ...base };
}
